import React, { lazy, Suspense } from "react";
import AddOns from "@Components/AddOns";
import Auth from "@Components/Auth";
import Loading from "@Components/loading";
import { union } from "lodash";
import { selectors } from "@ReduxStore/reducers";
import { useSelector } from "react-redux";
import { BrowserRouter, useRoutes, Navigate } from "react-router";
import { ROUTES } from "../constants";
import Interface from "./Interface";
import UserTimeoutCheck from "@Src/components/UserTimeoutCheck";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import useTermlyScriptHook from "@Src/components/Termly/useTermlyScriptHook";
import MediaQueriesProvider from "@Src/hooks/mediaQueriesContext";

const WithAuthentication = lazy(() => import("@Components/WithAuthentication"));
const Admin = lazy(() => import("./admin"));
const Members = lazy(() => import("./admin/members"));
const Billing = lazy(() => import("./admin/billing"));
const Director = lazy(() => import("./director/Director"));
const SavingsCalculator = lazy(() => import("./savingsEstimator"));
const GroceryCoupons = lazy(() => import("./grocery"));
const Deals = lazy(() => import("./deals/Deals"));
const Store = lazy(() => import("./store"));
const StoreLocation = lazy(() => import("./store/StoreLocation"));
const Account = lazy(() => import("./account"));
const Help = lazy(() => import("./help"));
const FavoritesManagement = lazy(() => import("./favorites"));
const Terms = lazy(() => import("./terms"));
const Travel = lazy(() => import("./travel"));
const Signout = lazy(() => import("./signout"));
const DealsFAQ = lazy(() => import("./FAQ"));
const Home = lazy(() => import("./home"));
const SsoRedirect = lazy(() => import("./ssoRedirect"));
const Unsubscribe = lazy(() => import("./unsubscribe"));

export default () => {
  const { _ } = useLingui();
  const isStandaloneTravel = useSelector(selectors.getIsStandaloneTravel);
  const isTravelEnabled = useSelector(selectors.getTravelEnabled);
  const programName = useSelector(selectors.getProgramName);
  useTermlyScriptHook();

  React.useEffect(() => {
    if (programName) {
      document.querySelector("title").textContent = _(
        msg`Find Deals, Discounts & More with ${programName}`,
      );
    }
  }, [programName, _]);

  return (
    <BrowserRouter>
      <MediaQueriesProvider>
        <Auth />
        <Suspense fallback={<Loading />}>
          <AppRoutes isStandaloneTravel={isStandaloneTravel} isTravelEnabled={isTravelEnabled} />
          <AddOns />
          <UserTimeoutCheck />
        </Suspense>
      </MediaQueriesProvider>
    </BrowserRouter>
  );
};

function AppRoutes({ isStandaloneTravel, isTravelEnabled }) {
  const travelTabs = useSelector(selectors.getTravelTabsConfig);
  const programKey = useSelector(selectors.getProgramKey);

  // build the shared routes - these routes should be wrapped by the "Interface"
  const sharedRoutes = [
    {
      path: ROUTES.ACCOUNT_PATH,
      element: <WithAuthentication />,
      children: [{ index: true, element: <Account /> }],
    },
    { path: ROUTES.HELP_PATH, element: <Help /> },
    { path: ROUTES.TERMS_PATH, element: <Terms /> },
    { path: ROUTES.DEALS_FAQ, element: <DealsFAQ /> },
    {
      path: ROUTES.FAVORITES_PATH,
      element: <WithAuthentication />,
      children: [{ index: true, element: <FavoritesManagement /> }],
    },
    {
      path: ROUTES.UNSUBSCRIBE_PATH,
      element: <WithAuthentication />,
      children: [{ index: true, element: <Unsubscribe /> }],
    },
  ];

  // build the shared travel related routes
  const themeparkRoutes = !travelTabs.theme_park_tab_enabled
    ? []
    : [
        {
          path: ROUTES.PARKS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];

  const dealsMerchantRoutes = [
    {
      path: ROUTES.RETAILER_PATH,
      element: <WithAuthentication />,
      children: [{ index: true, element: <Travel /> }],
    },
  ];
  const hotelsRoutes = !travelTabs.hotels_tab_enabled
    ? []
    : [
        {
          path: ROUTES.HOTELS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];
  const carsRoutes = !travelTabs.cars_tab_enabled
    ? []
    : [
        {
          path: ROUTES.CARS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];
  const activitiesRoutes = !travelTabs.activities_tab_enabled
    ? []
    : [
        {
          path: ROUTES.ACTIVITIES_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];
  const flightsRoutes = !travelTabs.flights_tab_enabled
    ? []
    : [
        {
          path: ROUTES.FLIGHTS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];

  const eventsRoutes = !travelTabs.events_tab_enabled
    ? []
    : [
        {
          path: ROUTES.EVENTS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
      ];
  const travelRoutes = !isTravelEnabled
    ? []
    : [
        {
          path: ROUTES.TRAVEL_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
        ...hotelsRoutes,
        ...carsRoutes,
        ...activitiesRoutes,
        ...flightsRoutes,
        ...themeparkRoutes,
        ...eventsRoutes,
        {
          path: ROUTES.TRAVEL_MY_TRIPS_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
        {
          path: `${ROUTES.TRAVEL_PATH}/*`,
          element: <Navigate replace to={ROUTES.TRAVEL_PATH} />,
        },
      ];

  // build the offers +/- travel routes.
  const excludedGroceryCouponPcids = process.env.REACT_APP_EXCLUDED_GROCERY_COUPONS_PCIDS;
  const groceryRoutes = excludedGroceryCouponPcids.includes(programKey)
    ? []
    : [
        {
          path: ROUTES.GROCERY_COUPONS_PATH,
          element: <GroceryCoupons />,
        },
      ];
  const dealsRoutes = [
    { path: ROUTES.SIGNOUT_PATH, element: <Signout /> },
    {
      path: ROUTES.HOME_PATH,
      element: <Interface />,
      children: [
        { path: ROUTES.HOME_PATH, element: <Home /> },
        {
          path: ROUTES.SAVINGS_CALCULATOR_PATH,
          element: <SavingsCalculator />,
        },
        { path: ROUTES.SEARCH_PATH, element: <Deals /> },
        ...sharedRoutes,
        ...travelRoutes,
        ...groceryRoutes,
        ...dealsMerchantRoutes,
        { path: ROUTES.STORE_PATH[0], element: <Store /> }, // store
        { path: ROUTES.STORE_PATH[1], element: <Store /> }, // offer
        { path: ROUTES.STORE_PATH[2], element: <StoreLocation /> }, // location
      ],
    },
  ];

  // build the travel-only routing
  const travelOnlyRoutes = [
    { path: ROUTES.SIGNOUT_PATH, element: <Signout /> },
    {
      path: ROUTES.HOME_PATH,
      element: <Interface />,
      children: [
        {
          path: ROUTES.HOME_PATH,
          element: <WithAuthentication />,
          children: [{ index: true, element: <Travel /> }],
        },
        ...travelRoutes,
        ...sharedRoutes,
      ],
    },
  ];

  // build the core routing base - these routes don't contain the main nav/footer etc.
  const routes = [
    { path: ROUTES.DIRECTOR_PATH, element: <Director /> },
    { path: ROUTES.REDIRECT_SSO, element: <SsoRedirect /> },

    {
      path: ROUTES.ADMIN_PATH,
      element: <WithAuthentication />,
      children: [
        {
          path: "*",
          element: <Admin />,
          children: [
            { path: "members", element: <Members /> },
            { path: "billing/*", element: <Billing /> },
            { path: "*", element: <Navigate replace to="members" /> },
          ],
        },
      ],
    },
    { path: "*", element: <Navigate replace to={ROUTES.HOME_PATH} /> },
  ];

  // construct the routes Component
  let AppRoutes = useRoutes(union(routes, isStandaloneTravel ? travelOnlyRoutes : dealsRoutes));

  return AppRoutes;
}
